import { useMemo, useState } from 'react';
import { Paper, Text, Stack, Group, SegmentedControl } from '@mantine/core';
import { DatePickerInput, type DatesRangeValue } from '@mantine/dates';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ZAxis,
} from 'recharts';
import type { TaskCompletion, TaskType } from '@/types/task';
import { TASK_TYPE_LABELS, TASK_TYPE_COLORS } from '@/types/task';

export function buildCategoryMapping(names: string[]): {
  list: string[];
  map: Map<string, number>;
} {
  const unique = Array.from(new Set(names)).sort();
  const map = new Map<string, number>();
  unique.forEach((name, i) => map.set(name, i));
  return { list: unique, map };
}

interface TaskActivityTimelineProps {
  completions: TaskCompletion[];
  dateRange: DatesRangeValue;
  onDateRangeChange: (range: DatesRangeValue) => void;
}

type TimelineView = 'activities' | 'equipment';

export function TaskActivityTimeline({
  completions,
  dateRange,
  onDateRangeChange,
}: TaskActivityTimelineProps) {
  const [timelineView, setTimelineView] = useState<TimelineView>('activities');

  // Get unique task types for scatter series
  const taskTypes = useMemo(() => {
    const uniqueTypes = new Set<TaskType>();
    completions.forEach((c) => {
      if (c.task_type) uniqueTypes.add(c.task_type);
    });

    return Array.from(uniqueTypes)
      .sort()
      .map((type) => ({
        value: type,
        label: TASK_TYPE_LABELS[type],
        color: TASK_TYPE_COLORS[type],
      }));
  }, [completions]);

  // Build stable category-to-index mappings so all Scatter series share
  // the same numeric Y positions (avoids Recharts category misalignment
  // when multiple Scatter series each have different category subsets).
  const activityCategories = useMemo(
    () => buildCategoryMapping(completions.map((c) => c.name || 'Unknown Task')),
    [completions]
  );

  const equipmentCategories = useMemo(
    () =>
      buildCategoryMapping(
        completions.filter((c) => c.equipment_name).map((c) => c.equipment_name!)
      ),
    [completions]
  );

  // Process for activities view
  const activitiesData = useMemo(() => {
    return completions.map((c) => {
      const taskName = c.name || 'Unknown Task';
      return {
        date: new Date(c.completed_at).getTime(),
        yIndex: activityCategories.map.get(taskName) ?? 0,
        taskName,
        taskType: c.task_type,
        taskTypeLabel: c.task_type ? TASK_TYPE_LABELS[c.task_type] : 'Unknown',
        notes: c.notes,
      };
    });
  }, [completions, activityCategories]);

  // Process for equipment view
  const equipmentData = useMemo(() => {
    const withEquipment = completions.filter((c) => c.equipment_name);

    return withEquipment.map((c) => {
      const equipment = c.equipment_name || '';
      return {
        date: new Date(c.completed_at).getTime(),
        yIndex: equipmentCategories.map.get(equipment) ?? 0,
        equipment,
        taskName: c.name || 'Unknown Task',
        taskType: c.task_type,
        taskTypeLabel: c.task_type ? TASK_TYPE_LABELS[c.task_type] : 'Unknown',
      };
    });
  }, [completions, equipmentCategories]);

  // Calculate unique items for height
  const uniqueItemsCount =
    timelineView === 'activities' ? activityCategories.list.length : equipmentCategories.list.length;

  const chartHeight = Math.min(2000, Math.max(300, uniqueItemsCount * 40));

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const chartData = timelineView === 'activities' ? activitiesData : equipmentData;
  const categories = timelineView === 'activities' ? activityCategories : equipmentCategories;

  if (completions.length === 0) {
    return (
      <Paper p="md" withBorder>
        <Text c="dimmed" ta="center" py="xl">
          No activity data in selected date range
        </Text>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Text size="lg" fw={600}>
            Activity Timeline
          </Text>
          <Group gap="md">
            <DatePickerInput
              type="range"
              placeholder="Pick dates range"
              value={dateRange}
              onChange={onDateRangeChange}
              maxDate={new Date()}
              clearable={false}
              valueFormat="MMM DD, YYYY"
              allowSingleDateInRange
              style={{ width: 280 }}
            />
            <SegmentedControl
              value={timelineView}
              onChange={(value) => setTimelineView(value as TimelineView)}
              data={[
                { label: 'Activities', value: 'activities' },
                { label: 'Equipment', value: 'equipment' },
              ]}
            />
          </Group>
        </Group>

        <ResponsiveContainer width="100%" height={chartHeight}>
          <ScatterChart>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              type="number"
              domain={['auto', 'auto']}
              tickFormatter={formatDate}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              dataKey="yIndex"
              type="number"
              domain={[-0.5, categories.list.length - 0.5]}
              ticks={categories.list.map((_, i) => i)}
              allowDecimals={false}
              tickFormatter={(value: number) => categories.list[Math.round(value)] ?? ''}
              width={200}
              tick={{ fontSize: 12 }}
            />
            <ZAxis range={[60, 60]} />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              content={({ payload }) => {
                if (!payload || payload.length === 0) return null;
                const data = payload[0]?.payload;
                if (!data) return null;

                return (
                  <Paper p="xs" withBorder>
                    <Stack gap={4}>
                      <Text size="sm" fw={600}>
                        {formatDate(data.date)}
                      </Text>
                      <Text size="sm" c="dimmed">
                        Task: {data.taskName}
                      </Text>
                      <Text size="sm" c="dimmed">
                        Type: {data.taskTypeLabel}
                      </Text>
                      {timelineView === 'equipment' && data.equipment && (
                        <Text size="sm" c="dimmed">
                          Equipment: {data.equipment}
                        </Text>
                      )}
                      {data.notes && (
                        <Text size="sm" c="dimmed">
                          Notes: {data.notes}
                        </Text>
                      )}
                    </Stack>
                  </Paper>
                );
              }}
            />
            <Legend />
            {taskTypes.map((taskType) => (
              <Scatter
                key={taskType.value}
                name={taskType.label}
                data={chartData.filter((d) => d.taskType === taskType.value)}
                fill={`var(--mantine-color-${taskType.color}-6)`}
              />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      </Stack>
    </Paper>
  );
}
